import { SetOptional } from 'type-fest';

import { BaseItem, NullablePartial } from './common';
import * as PracticePreference from './practice/preference';

export enum Kind {
  Inpatient = 'INPATIENT',
  Outpatient = 'OUTPATIENT',
}

export enum BillingType {
  Hospital = 'HOSPITAL',
  Provider = 'PROVIDER',
}

export interface BasePractice {
  name: string;
  internalName: string | null;
  address: string;
  city: string;
  state: string;
  zip: string;
  phoneNumber: string;
  timezone: string;
  longitude: number | null;
  latitude: number | null;
  practiceGroupId: string;
  ehrSystemId: string | null;
  previsitDomain: string | null;
  kind: Kind;
  taxId: string | null;
  isTest: boolean | null;
  isBroadcast: boolean | null;
  billingType: BillingType | null;
  isInternal: boolean | null;
}

export interface Practice extends BaseItem, BasePractice {}
export type PracticeFull = Practice & {
  practiceGroupName: string;
  organizationId: string;
  organizationName: string;
};

export type CreateValues = SetOptional<NullablePartial<BasePractice>, 'kind'>;
export type UpdateValues = Partial<BasePractice>;

export interface ProviderPractice extends BaseItem {
  providerUserId: string;
  practiceId: string;
}

export interface PracticeAccount extends BaseItem {
  practiceId: string;
  accountId: string;
  livemode: boolean;
}

export interface PracticeCsvImportPreferences
  extends Pick<
    PracticePreference.Preferences,
    | 'forcePrevisitZipCodeAuth'
    | 'exportPrevisitPatientUploadStatusesEnabled'
    | 'exportPrevisitPatientUploadsEnabled'
    | 'exportResidualUpdatedPrevisitPatientInfoClinicalCommentEnabled'
    | 'exportUpdatedPrevisitFamilyMedicalHistoryEnabled'
    | 'exportUpdatedPrevisitPatientAllergiesEnabled'
    | 'exportUpdatedPrevisitPatientInfoGeneralCommentEnabled'
    | 'exportUpdatedPrevisitPatientInformationEnabled'
    | 'exportUpdatedPrevisitPatientMedicalHistoryEnabled'
    | 'exportUpdatedSocialHistoryEnabled'
  > {}

export type PracticeBulkCreateValues = CreateValues &
  Partial<PracticeCsvImportPreferences>;
export const PRACTICE_IMPORT_COLUMNS: (keyof PracticeBulkCreateValues)[] = [
  'name',
  'address',
  'city',
  'state',
  'zip',
  'longitude',
  'latitude',
  'phoneNumber',
  'timezone',
  'practiceGroupId',
  'ehrSystemId',
  'previsitDomain',
  'kind',
  'isTest',
  'billingType',
  'isInternal',
  'forcePrevisitZipCodeAuth',
  'exportPrevisitPatientUploadStatusesEnabled',
  'exportPrevisitPatientUploadsEnabled',
  'exportResidualUpdatedPrevisitPatientInfoClinicalCommentEnabled',
  'exportUpdatedPrevisitFamilyMedicalHistoryEnabled',
  'exportUpdatedPrevisitPatientAllergiesEnabled',
  'exportUpdatedPrevisitPatientInfoGeneralCommentEnabled',
  'exportUpdatedPrevisitPatientInformationEnabled',
  'exportUpdatedPrevisitPatientMedicalHistoryEnabled',
  'exportUpdatedSocialHistoryEnabled',
];

export type FindAllOptions = Partial<
  Pick<Practice, 'isBroadcast'> & { organizationId: string }
>;

export type SafePractice = Pick<
  Practice,
  'name' | 'address' | 'city' | 'phoneNumber' | 'state' | 'timezone' | 'zip'
>;

export type SafePracticeWithDistance = SafePractice & {
  distance?: number;
  practiceId: string;
};
